import { LineChartRecharts } from '.';
import { FC } from 'react';
import { StackedBarChartRecharts } from './StackedBarChartRecharts';
import { BarChartRecharts } from './BarChartRecharts';
import { ComposedChartRecharts } from './ComposedChartRecharts';
import { PieChartRecharts } from './PieChartRecharts';
import { HrStackedBarChartRecharts } from './HrStackedBarChartRecharts';
import { ComboChartRecharts } from './ComboChartRecharts';
import { HrComboChartRecharts } from './HrComboChartRecharts';
import { PercentageChart } from './PercentageChart';
import { StackedComboChart } from './StackedComboChart';
import { BubbleChartRecharts } from './BubbleChartRecharts';
import ScopeChangeChart from './MultiBarChart';
import { HrBarChart } from './HrBarChart';
import { CategoryBarChart } from './CategoryBarChart';
import { ControlChart } from './ControlChart';

interface ChartProps {
  chartref?: any;
  data: any;
  keys: any;
  chartMetadata: any;
  showLabel?: boolean;
  selectedTeam?: string;
  hideBtn?: boolean;
  slant?: boolean;
  granularSlant?: boolean;
  forDetails?: boolean;
  ticketType?: string;
  selected?: any;
  queryKey?: string;
  repos?: string[];
  showYLabel?: boolean;
  showXLabel?: boolean;
  showDownload?: boolean;
  detailView?: boolean;
  refLines?: any;
}

export const Chart: FC<ChartProps> = ({
  chartref = null,
  data,
  keys,
  chartMetadata,
  showLabel,
  selectedTeam,
  hideBtn,
  slant,
  granularSlant,
  forDetails,
  ticketType,
  selected,
  queryKey,
  repos,
  showYLabel,
  showXLabel,
  showDownload,
  detailView,
  refLines,
}) => {
  switch (chartMetadata?.chartType) {
    case 'line_chart':
    case 'breakdown_stacked_bar_chart':
      return (
        <LineChartRecharts
          chartref={chartref}
          data={data}
          keys={keys}
          chartMetadata={chartMetadata}
          showLabel={showLabel}
          selected={selected}
          selectedTeam={selectedTeam || ''}
          hideBtn={hideBtn}
          slant={slant}
          granularSlant={granularSlant}
          ticketType={ticketType}
          forDetails={forDetails}
          queryKey={queryKey}
          repos={repos}
          showYLabel={showYLabel}
          showXLabel={showXLabel}
          detailView={detailView}
        />
      );

    case 'bar_chart':
      if (chartMetadata?.chartKey === 'ISSUE_PROGRESSION_RATE') {
        return (
          <CategoryBarChart
            data={data?.statusDistribution?.map((item: any) => ({
              ...item,
              name: item.label,
              value: item.value ? Number(item.value) : 0,
            }))}
            keys={keys}
            showLabel={showLabel}
            selected={selected}
            chartMetadata={chartMetadata}
            selectedTeam={selectedTeam || ''}
            ticketType={ticketType}
            queryKey={queryKey}
            repos={repos}
          />
        );
      } else {
        return (
          <BarChartRecharts
            data={data}
            keys={keys}
            showLabel={showLabel}
            chartMetadata={chartMetadata}
            slant={slant}
            selected={selected}
            granularSlant={granularSlant}
            selectedTeam={selectedTeam}
            repos={repos}
            type={ticketType}
            showYLabel={showYLabel}
            showXLabel={showXLabel}
            detailView={detailView}
            hideBtn={hideBtn}
          />
        );
      }

    case 'stacked_bar_chart':
      return (
        <StackedBarChartRecharts
          data={data}
          keys={keys}
          chartMetadata={chartMetadata}
          selected={selected}
          repos={repos || []}
          selectedTeam={selectedTeam || ''}
          slant={slant}
          granularSlant={granularSlant}
        />
      );

    case 'stacked_bar_and_line_chart':
      return (
        <StackedComboChart
          data={data}
          keys={keys}
          showLabel={showLabel}
          selected={selected}
          granularSlant={granularSlant}
          chartMetadata={chartMetadata}
          selectedTeam={selectedTeam || ''}
          forDetails={forDetails}
          repos={repos}
          showYLabel={showYLabel}
          showXLabel={showXLabel}
          detailView={detailView}
        />
      );

    case 'combo_chart':
      return (
        <ComboChartRecharts
          data={data}
          keys={keys}
          showLabel={showLabel}
          chartMetadata={chartMetadata}
          selectedTeam={selectedTeam || ''}
          forDetails={forDetails}
          hideBtn={true}
          selected={selected}
          granularSlant={granularSlant}
          ticketType={ticketType}
          queryKey={queryKey}
          repos={repos}
          showXLabel={showXLabel}
          showYLabel={showYLabel}
        />
      );
    case 'bar_and_line_chart':
      return (
        <ComposedChartRecharts
          data={data}
          keys={keys}
          showLabel={true}
          selected={selected}
          chartMetadata={chartMetadata}
          selectedTeam={selectedTeam || ''}
          granularSlant={granularSlant}
          forDetails={forDetails}
          ticketType={ticketType}
          repos={repos}
          queryKey={queryKey}
          hideBtn={hideBtn}
          showDownload={showDownload}
          showXLabel={showXLabel}
          detailView={detailView}
        />
      );

    case 'reverse_combo_chart':
      return (
        <HrComboChartRecharts
          data={data}
          keys={keys}
          showLabel={true}
          selected={selected}
          chartMetadata={chartMetadata}
          selectedTeam={selectedTeam || ''}
          forDetails={forDetails}
          ticketType={ticketType}
          repos={repos}
        />
      );

    case 'horizontal_stacked_bar_chart':
      return (
        <HrStackedBarChartRecharts
          data={data}
          keys={keys}
          chartMetadata={chartMetadata}
          forDetails={forDetails}
          selected={selected}
          selectedTeam={selectedTeam || ''}
          hideBtn={true}
          ticketType={ticketType || ''}
          queryKey={queryKey}
          repos={repos}
        />
      );

    case 'pie_chart':
      return (
        <PieChartRecharts
          data={
            chartMetadata?.chartKey === 'ISSUE_PROGRESSION_RATE'
              ? data?.statusDistribution?.map((item: any) => ({
                  ...item,
                  name: item.label,
                  value: item.value ? Number(item.value) : 0,
                }))
              : data
          }
          showLabel={showLabel}
          selected={selected}
          chartMetadata={chartMetadata}
          selectedTeam={selectedTeam || ''}
          ticketType={ticketType}
          queryKey={queryKey}
          repos={repos}
        />
      );
    case 'bubble_chart':
      return (
        <BubbleChartRecharts
          data={data}
          keys={keys}
          showLabel={showLabel}
          selected={selected}
          chartMetadata={chartMetadata}
          selectedTeam={selectedTeam || ''}
          ticketType={ticketType}
          queryKey={queryKey}
          repos={repos}
        />
      );

    case 'percentage_chart':
      return (
        <PercentageChart
          data={data}
          showLabel={showLabel}
          selected={selected}
          chartMetadata={chartMetadata}
          selectedTeam={selectedTeam || ''}
          repos={repos}
        />
      );
    case 'multi_bar_chart':
      return <ScopeChangeChart data={data} showLabel={showLabel} />;

    case 'horizontal_bar_chart':
      return (
        <HrBarChart data={data} keys={keys} chartMetadata={chartMetadata} />
      );

    case 'control_chart':
      return (
        <ControlChart
          chartref={chartref}
          data={data}
          keys={keys}
          chartMetadata={chartMetadata}
          showLabel={showLabel}
          selected={selected}
          selectedTeam={selectedTeam || ''}
          hideBtn={hideBtn}
          slant={slant}
          granularSlant={granularSlant}
          ticketType={ticketType}
          forDetails={forDetails}
          queryKey={queryKey}
          repos={repos}
          showYLabel={showYLabel}
          showXLabel={showXLabel}
          detailView={detailView}
          refLines={refLines}
        />
      );

    default:
      return <div />;
  }
};

export default Chart;
